export const GENDER = {
  MALE: 'M',
  FEMALE: 'F',
  UNKNOWN: 'U',
} as const

export const GENDER_OPTIONS = [
  {
    value: GENDER.MALE,
    label: 'MALE',
  },
  {
    value: GENDER.FEMALE,
    label: 'FEMALE',
  },
  {
    value: GENDER.UNKNOWN,
    label: 'UNKNOWN',
  },
] as const

export const USER_STATUS = {
  ENABLED: 'enabled',
  LOCKED: 'locked',
  ALL: 'all'
} as const

export const WORKSTATION_SELECTION = {
  ALL: 'ALL'
} as const

export const USER_ROLES = {
  SUPERADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  USER: 'User',
  VIEWER: 'Viewer'
} as const

export const USER_PERMISSIONS = {
  CREATE_USER: 'CREATE_USER',
  DELETE_USER: 'DELETE_USER',
  EDIT_USER: 'EDIT_USER',
  VIEW_USER: 'VIEW_USER',
  
  VIEW_TRANSACTIONS: 'VIEW_TRANSACTIONS',
  CREATE_TRANSACTION: 'CREATE_TRANSACTION',
  EDIT_TRANSACTION: 'EDIT_TRANSACTION',
  DELETE_TRANSACTION: 'DELETE_TRANSACTION',
  
  VIEW_REPORTS: 'VIEW_REPORTS',
  CREATE_REPORT: 'CREATE_REPORT',
  EDIT_REPORT: 'EDIT_REPORT',
  DELETE_REPORT: 'DELETE_REPORT',

  VIEW_ALL_LOCATIONS: 'VIEW_ALL_LOCATIONS',
  
  VIEW_WORKSTATIONS: 'VIEW_WORKSTATIONS',
  CREATE_WORKSTATIONS: 'CREATE_WORKSTATIONS',
  EDIT_WORKSTATIONS: 'EDIT_WORKSTATIONS',
  DELETE_WORKSTATIONS: 'DELETE_WORKSTATIONS',
    
  // Organization Management
  CREATE_ORGANIZATION: 'CREATE_ORGANIZATION',
  EDIT_ORGANIZATION: 'EDIT_ORGANIZATION',
  DELETE_ORGANIZATION: 'DELETE_ORGANIZATION',
  VIEW_ORGANIZATION: 'VIEW_ORGANIZATION',
} as const

export const ROLE_DESCRIPTIONS = {
  [USER_ROLES.SUPERADMIN]: 'Full access to all features, including user management, creation, deletion, and modification.',
  [USER_ROLES.ADMIN]: 'Full access to all features except user creation and deletion. Admin can manage users, transactions, reports, and locations.',
  [USER_ROLES.VIEWER]: 'View transactions and reports of all locations. Cannot create, edit, or delete data.',
  [USER_ROLES.USER]: "Can view the assigned location's transactions and workstations only. Cannot create, edit, or delete data."
} as const

export const ROLE_SUMMARY = {
  [USER_ROLES.SUPERADMIN]: 'Full control',
  [USER_ROLES.ADMIN]: 'Full control except user modification',
  [USER_ROLES.VIEWER]: 'View transactions and reports of all locations',
  [USER_ROLES.USER]: "View the assigned location's transactions and workstations"
} as const

export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPERADMIN]: Object.values(USER_PERMISSIONS),
  [USER_ROLES.ADMIN]: Object.values(USER_PERMISSIONS).filter(p => 
    p !== USER_PERMISSIONS.CREATE_USER && p !== USER_PERMISSIONS.DELETE_USER
  ),
  [USER_ROLES.VIEWER]: [
    USER_PERMISSIONS.VIEW_TRANSACTIONS,
    USER_PERMISSIONS.VIEW_REPORTS,
  ],
  [USER_ROLES.USER]: [
    USER_PERMISSIONS.VIEW_TRANSACTIONS,
  ]
} as const

export type UserStatus = typeof USER_STATUS[keyof typeof USER_STATUS]
export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES]
export type UserPermission = typeof USER_PERMISSIONS[keyof typeof USER_PERMISSIONS]

export const PERMISSION_NUMBER_TO_KEY: Record<number, UserPermission> = {
  1: USER_PERMISSIONS.CREATE_USER,
  2: USER_PERMISSIONS.DELETE_USER,
  3: USER_PERMISSIONS.EDIT_USER,
  4: USER_PERMISSIONS.VIEW_USER,
  5: USER_PERMISSIONS.VIEW_TRANSACTIONS,
  6: USER_PERMISSIONS.CREATE_TRANSACTION,
  7: USER_PERMISSIONS.EDIT_TRANSACTION,
  8: USER_PERMISSIONS.DELETE_TRANSACTION,
  9: USER_PERMISSIONS.VIEW_REPORTS,
  10: USER_PERMISSIONS.CREATE_REPORT,
  11: USER_PERMISSIONS.EDIT_REPORT,
  12: USER_PERMISSIONS.DELETE_REPORT,
  13: USER_PERMISSIONS.VIEW_WORKSTATIONS,
  14: USER_PERMISSIONS.CREATE_WORKSTATIONS,
  15: USER_PERMISSIONS.EDIT_WORKSTATIONS,
  16: USER_PERMISSIONS.DELETE_WORKSTATIONS,
  17: USER_PERMISSIONS.CREATE_ORGANIZATION,
  18: USER_PERMISSIONS.EDIT_ORGANIZATION,
  19: USER_PERMISSIONS.DELETE_ORGANIZATION,
  20: USER_PERMISSIONS.VIEW_ORGANIZATION,
  21: USER_PERMISSIONS.VIEW_ALL_LOCATIONS,
}

export const PERMISSION_KEY_TO_NUMBER: Record<UserPermission, number> = {
  [USER_PERMISSIONS.CREATE_USER]: 1,
  [USER_PERMISSIONS.DELETE_USER]: 2,
  [USER_PERMISSIONS.EDIT_USER]: 3,
  [USER_PERMISSIONS.VIEW_USER]: 4,
  [USER_PERMISSIONS.VIEW_TRANSACTIONS]: 5,
  [USER_PERMISSIONS.CREATE_TRANSACTION]: 6,
  [USER_PERMISSIONS.EDIT_TRANSACTION]: 7,
  [USER_PERMISSIONS.DELETE_TRANSACTION]: 8,
  [USER_PERMISSIONS.VIEW_REPORTS]: 9,
  [USER_PERMISSIONS.CREATE_REPORT]: 10,
  [USER_PERMISSIONS.EDIT_REPORT]: 11,
  [USER_PERMISSIONS.DELETE_REPORT]: 12,
  [USER_PERMISSIONS.VIEW_WORKSTATIONS]: 13,
  [USER_PERMISSIONS.CREATE_WORKSTATIONS]: 14,
  [USER_PERMISSIONS.EDIT_WORKSTATIONS]: 15,
  [USER_PERMISSIONS.DELETE_WORKSTATIONS]: 16,
  [USER_PERMISSIONS.CREATE_ORGANIZATION]: 17,
  [USER_PERMISSIONS.EDIT_ORGANIZATION]: 18,
  [USER_PERMISSIONS.DELETE_ORGANIZATION]: 19,
  [USER_PERMISSIONS.VIEW_ORGANIZATION]: 20,
  [USER_PERMISSIONS.VIEW_ALL_LOCATIONS]: 21,
}

export function mapPermissionNumbersToKeys(values: number[]): UserPermission[] {
  return values
    .map((n) => PERMISSION_NUMBER_TO_KEY[n])
    .filter((v): v is UserPermission => Boolean(v))
}

export function mapPermissionKeysToNumbers(values: UserPermission[]): number[] {
  return values.map((k) => PERMISSION_KEY_TO_NUMBER[k])
}
